<template>
  <div class="page-wrapper">
    <div class="bg-orb bg-orb-1"></div>
    <div class="bg-orb bg-orb-2"></div>
    <div class="bg-cross"></div>

    <div class="card-container">
      <div class="card">

        <!-- Logo -->
        <router-link to="/" class="logo-link">
          <img
            class="logo"
            src="https://res.cloudinary.com/dldd8ucby/image/upload/v1587167979/RA/WhatsApp_Image_2020-04-16_at_10.51.15_AM.jpg"
            alt="Logo"
          />
        </router-link>

        <!-- Header -->
        <div class="header">
          <h1 class="title">Add Question</h1>
          <p class="subtitle">Fill in the details to add a new exam question</p>
        </div>

        <form @submit.prevent="submit" class="form">

          <!-- Rank -->
          <div class="field">
            <label>Rank</label>
            <div class="input-wrapper select-wrapper">
              <span class="input-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              </span>
              <select v-model="data.rank">
                <option disabled value="undefined">Select a rank</option>
                <option v-for="(rank, i) in ranks" :key="i" :value="rank.value">{{ rank.name }}</option>
              </select>
              <span class="select-arrow">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </span>
            </div>
            <p v-if="errors.rank" class="error-msg">Please select a rank.</p>
          </div>

          <!-- Question -->
          <div class="field">
            <label>Question</label>
            <div class="textarea-wrapper">
              <textarea
                class="answer-textarea"
                rows="3"
                placeholder="e.g. Who is the president of Nigeria?"
                v-model="data.question"
              />
            </div>
          </div>

          <!-- Options -->
          <div class="options-grid">
            <div class="field" v-for="opt in ['a','b','c','d']" :key="opt">
              <label>Option {{ opt.toUpperCase() }}</label>
              <div class="input-wrapper">
                <span class="input-icon option-key-icon">{{ opt.toUpperCase() }}</span>
                <input
                  type="text"
                  :placeholder="optionPlaceholders[opt]"
                  v-model="data[`option_${opt}`]"
                />
              </div>
            </div>
          </div>

          <!-- Correct Answer -->
          <div class="field">
            <label>Correct Answer</label>
            <div class="input-wrapper select-wrapper">
              <span class="input-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              <select v-model="data.answer">
                <option disabled value="undefined">Select the correct answer</option>
                <option v-for="(opt, i) in options" :key="i" :value="opt.value">Option {{ opt.name }}</option>
              </select>
              <span class="select-arrow">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </span>
            </div>
          </div>

          <!-- Answer preview -->
          <div class="answer-preview" v-if="data.answer && data[`option_${data.answer}`]">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Correct answer: <strong>{{ data[`option_${data.answer}`] }}</strong>
          </div>

          <vue-element-loading v-if="loading" :active="loading" :is-full-screen="true" />

          <button type="submit" class="submit-btn" :disabled="loading">
            <span v-if="!loading">
              Add Question
            </span>
            <span v-else class="loading-text">
              <svg class="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" class="opacity-75"/></svg>
              Saving…
            </span>
          </button>

        </form>

        <p class="footer-text">&copy; G1BRA</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  data() {
    return {
      data: {},
      loading: false,
      errors: {},
      disabled: false,
      optionPlaceholders: {
        a: "e.g. Buhari",
        b: "e.g. Jonathan",
        c: "e.g. Obasanjo",
        d: "e.g. Tinubu",
      },
      options: [
        { name: "A", value: "a" },
        { name: "B", value: "b" },
        { name: "C", value: "c" },
        { name: "D", value: "d" },
      ],
      ranks: [
        { name: "Assistant Intern",  value: "ass-intern" },
        { name: "Intern",            value: "intern" },
        { name: "Senior Intern",     value: "sen-intern" },
        { name: "Envoy",             value: "envoy" },
        { name: "Special Envoy",     value: "spe-envoy" },
        { name: "Senior Envoy",      value: "sen-envoy" },
        { name: "Dean",              value: "dean" },
        { name: "Test",              value: "test" },
      ],
    };
  },
  methods: {
    ...mapActions(["addQuestion"]),
    async submit() {
      if (
        !this.data.rank     ||
        !this.data.question ||
        !this.data.answer   ||
        !this.data.option_a ||
        !this.data.option_b ||
        !this.data.option_c ||
        !this.data.option_d
      ) {
        this.$toasted.error("All fields are required.").goAway(2500);
        return;
      }
      this.loading = true;
      try {
        let res = await this.addQuestion(this.data);
        if (res === true) {
          this.loading = false;
          this.$toasted.success("Question added successfully.").goAway(2500);
          this.data = {};
        } else {
          this.loading = false;
          this.$toasted.error(res.data.message).goAway(2500);
        }
      } catch (err) {
        console.log(err);
        this.$toasted.error("An error occurred.").goAway(2500);
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Jost:wght@300;400;500;600&display=swap');

* { box-sizing: border-box; }

/* ── Page ── */
.page-wrapper {
  min-height: 100vh;
  background: #0f1923;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  font-family: 'Jost', sans-serif;
  position: relative;
  overflow: hidden;
}

/* ── Background ── */
.bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  opacity: 0.18;
}
.bg-orb-1 { width: 500px; height: 500px; background: radial-gradient(circle, #c8a96e, transparent); top: -150px; right: -100px; }
.bg-orb-2 { width: 400px; height: 400px; background: radial-gradient(circle, #3a5f8a, transparent); bottom: -100px; left: -80px; }
.bg-cross {
  position: absolute;
  width: 180px; height: 180px;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.03;
  background:
    linear-gradient(#c8a96e 0 0) 50% 20%/20px 100%,
    linear-gradient(#c8a96e 0 0) 20% 35%/100% 20px;
  background-repeat: no-repeat;
}

/* ── Card ── */
.card-container { width: 100%; max-width: 560px; position: relative; z-index: 1; }
.card {
  background: #fff;
  border-radius: 16px;
  padding: 2.5rem 2.5rem 2rem;
  box-shadow:
    0 0 0 1px rgba(200,169,110,0.15),
    0 24px 60px rgba(0,0,0,0.45),
    0 4px 16px rgba(0,0,0,0.3);
  animation: slideUp 0.5s cubic-bezier(0.22,1,0.36,1) both;
}
@keyframes slideUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Logo ── */
.logo-link { display: block; text-decoration: none; margin-bottom: 1.5rem; }
.logo {
  display: block; width: 72px; height: 72px;
  object-fit: contain; margin: 0 auto;
  border-radius: 50%;
  box-shadow: 0 4px 16px rgba(200,169,110,0.25);
}

/* ── Header ── */
.header { text-align: center; margin-bottom: 1.75rem; }
.title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 2rem; font-weight: 700;
  color: #1a1a2e; margin: 0 0 0.35rem;
  letter-spacing: 0.01em;
}
.subtitle { font-size: 0.85rem; color: #8a8fa8; font-weight: 300; margin: 0; }
.header::after {
  content: ''; display: block;
  width: 40px; height: 2px;
  background: linear-gradient(90deg, #c8a96e, #e8c98a);
  border-radius: 2px; margin: 0.85rem auto 0;
}

/* ── Form ── */
.form { display: flex; flex-direction: column; gap: 1.1rem; }
.field { display: flex; flex-direction: column; gap: 0.4rem; }

label {
  font-size: 0.72rem; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: #4a4f6a;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.input-icon {
  position: absolute; left: 0.85rem;
  display: flex; align-items: center;
  color: #b0b5cc; pointer-events: none;
}
.input-icon svg { width: 15px; height: 15px; }

/* Letter icon for options */
.option-key-icon {
  width: 20px; height: 20px;
  background: #f0f2f8;
  color: #4a4f6a;
  border-radius: 5px;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.7rem; font-weight: 700;
  left: 0.65rem;
}

input, select, .answer-textarea {
  width: 100%;
  font-family: 'Jost', sans-serif;
  font-size: 0.9rem; font-weight: 400;
  color: #1a1a2e;
  background: #f7f8fc;
  border: 1.5px solid #e2e5f0;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  appearance: none; -webkit-appearance: none;
}
input { padding: 0.65rem 0.9rem 0.65rem 2.5rem; }
input::placeholder, .answer-textarea::placeholder { color: #b8bdce; }
input:focus, select:focus, .answer-textarea:focus {
  border-color: #c8a96e;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(200,169,110,0.12);
}

/* Textarea */
.textarea-wrapper { position: relative; }
.answer-textarea { padding: 0.75rem 1rem; resize: vertical; }

/* Select */
.select-wrapper { position: relative; }
select { padding: 0.65rem 0.9rem 0.65rem 2.5rem; }
.select-arrow {
  position: absolute; right: 0.9rem;
  display: flex; align-items: center;
  color: #b0b5cc; pointer-events: none;
}
.select-arrow svg { width: 14px; height: 14px; }

/* Options 2-col grid */
.options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
}

/* Answer preview */
.answer-preview {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.9rem;
  background: #edf7f2;
  border: 1.5px solid #b8e2cc;
  border-radius: 9px;
  font-size: 0.85rem;
  color: #2d7a4f;
}
.answer-preview svg { width: 15px; height: 15px; flex-shrink: 0; }

/* Error */
.error-msg { font-size: 0.75rem; color: #e05252; margin: 0; }

/* ── Submit Button ── */
.submit-btn {
  margin-top: 0.4rem;
  width: 100%;
  padding: 0.8rem 1.5rem;
  background: linear-gradient(135deg, #1a2a3e, #243650);
  color: #fff;
  font-family: 'Jost', sans-serif;
  font-size: 0.9rem; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase;
  border: none; border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 4px 16px rgba(26,42,62,0.3);
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
}
.submit-btn svg { width: 16px; height: 16px; transition: transform 0.2s; }
.submit-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #c8a96e, #e8c98a);
  box-shadow: 0 6px 24px rgba(200,169,110,0.4);
  transform: translateY(-1px);
}
.submit-btn:disabled { opacity: 0.65; cursor: not-allowed; }

.loading-text { display: flex; align-items: center; gap: 0.5rem; }
.spinner { width: 18px; height: 18px; animation: spin 0.8s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* ── Footer ── */
.footer-text {
  text-align: center; font-size: 0.72rem;
  color: #c0c5d8; margin: 1.5rem 0 0;
  letter-spacing: 0.05em;
}

/* ── Responsive ── */
@media (max-width: 500px) {
  .card { padding: 2rem 1.5rem 1.75rem; }
  .title { font-size: 1.75rem; }
  .options-grid { grid-template-columns: 1fr; }
}
</style>